import { useState, useEffect } from 'react';
import { useAuth, AuthGuard } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import BenimAdimim from './components/BenimAdimim';
import Ilk4Kisim from './components/Ilk4Kisim';
import Step6Plani from './components/Step6Plani';
import Step6PlanNedir from './components/Step6PlanNedir';
import LiderVideolari from './components/LiderVideolari';
import WhatsAppAksiyonlari from './components/WhatsAppAksiyonlari';
import Ayarlar from './components/Ayarlar';
import { MotivasyonVizyon, EkipAnalizi, RiskMerkezi } from './components/ExtraPages';
import { mockSettings, Settings } from '../../mocks/step6Data';

type Section = 'dashboard' | 'benim-adimim' | 'ilk4' | 'step6-plani' | 'step6-nedir' | 'whatsapp' | 'motivasyon' | 'lider-videolari' | 'ekip-analizi' | 'risk-merkezi' | 'ayarlar';

const sectionTitles: Record<Section, string> = {
  dashboard: 'Dashboard',
  'benim-adimim': 'Benim Adımım',
  ilk4: 'İlk 4 Kişim',
  'step6-plani': 'STEP6 Planım',
  'step6-nedir': 'STEP6 Planı Nedir?',
  whatsapp: 'WhatsApp Aksiyonları',
  motivasyon: 'Motivasyon & Vizyon',
  'lider-videolari': 'Lider Videoları',
  'ekip-analizi': 'Ekip Analizi',
  'risk-merkezi': 'Risk Merkezi',
  ayarlar: 'Ayarlar',
};

function HomeContent() {
  const { profile, signOut } = useAuth();
  const [activeSection, setActiveSection] = useState<Section>('dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [settings, setSettings] = useState<Settings>(mockSettings);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const userRole = (profile?.role as 'kullanici' | 'lider' | 'admin') || 'kullanici';

  // Load settings from Supabase
  useEffect(() => {
    if (!profile) {
      setSettings(mockSettings);
      setSettingsLoading(false);
      return;
    }

    let cancelled = false;

    supabase
      .from('step6_settings')
      .select('*')
      .eq('user_id', profile.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setSettings(mockSettings);
        } else {
          setSettings({
            aktiflikBedeli: data.aktiflik_bedeli,
            kayitPrimi: data.kayit_primi,
            seviye1Kazanc: data.seviye1_kazanc,
            seviye2Kazanc: data.seviye2_kazanc,
            seviye3Kazanc: data.seviye3_kazanc,
            seviye4Kazanc: data.seviye4_kazanc,
            seviye5Kazanc: data.seviye5_kazanc,
            seviye6Kazanc: data.seviye6_kazanc,
          });
        }
        setSettingsLoading(false);
      });

    return () => { cancelled = true; };
  }, [profile]);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(false);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const desktopSidebarWidth = collapsed ? 64 : 240;
  const contentMarginLeft = isMobile ? 0 : desktopSidebarWidth;

  const navigate = (s: string) => {
    setActiveSection(s as Section);
    setMobileOpen(false);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard': return <Dashboard onNavigate={navigate} settings={settings} />;
      case 'benim-adimim': return <BenimAdimim onNavigate={navigate} />;
      case 'ilk4': return <Ilk4Kisim userRole={userRole} />;
      case 'step6-plani': return <Step6Plani settings={settings} />;
      case 'step6-nedir': return <Step6PlanNedir onNavigate={navigate} />;
      case 'whatsapp': return <WhatsAppAksiyonlari />;
      case 'motivasyon': return <MotivasyonVizyon />;
      case 'lider-videolari': return <LiderVideolari />;
      case 'ekip-analizi': return <EkipAnalizi />;
      case 'risk-merkezi': return <RiskMerkezi />;
      case 'ayarlar': return <Ayarlar settings={settings} onSettingsChange={setSettings} />;
      default: return <Dashboard onNavigate={navigate} settings={settings} />;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#080A10', fontFamily: "'Inter', sans-serif" }}>

      {mobileOpen && isMobile && (
        <div
          className="fixed inset-0 z-30 bg-black/70"
          style={{ backdropFilter: 'blur(4px)' }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        className="fixed top-0 left-0 h-full z-40 transition-transform duration-300"
        style={{
          transform: isMobile
            ? mobileOpen ? 'translateX(0)' : 'translateX(-100%)'
            : 'translateX(0)',
          width: isMobile ? 240 : desktopSidebarWidth,
        }}
      >
        <Sidebar
          activeSection={activeSection}
          onNavigate={navigate}
          userRole={userRole}
          collapsed={isMobile ? false : collapsed}
          onToggle={() => {
            if (isMobile) setMobileOpen(false);
            else setCollapsed(c => !c);
          }}
          onSignOut={signOut}
        />
      </div>

      <div
        className="flex flex-col min-h-screen transition-all duration-300"
        style={{ marginLeft: contentMarginLeft }}
      >
        <header
          className="sticky top-0 z-20 flex items-center gap-3 px-4 md:px-6 py-3 md:py-4"
          style={{
            background: 'rgba(8,10,16,0.92)',
            backdropFilter: 'blur(14px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {isMobile && (
            <button
              onClick={() => setMobileOpen(true)}
              className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <i className="ri-menu-2-line text-gray-300 text-lg" />
            </button>
          )}

          {isMobile && (
            <div className="flex items-center gap-2">
              <img
                src="https://public.readdy.ai/ai/img_res/bd011a02-ab2e-435b-9429-0067149db8ff.png"
                alt="STEP6"
                className="w-7 h-7 rounded-lg object-contain"
              />
              <span className="text-sm font-bold text-white">STEP6</span>
            </div>
          )}

          <div className="flex-1 min-w-0">
            {!isMobile && (
              <h1 className="text-base font-bold text-white truncate">{sectionTitles[activeSection]}</h1>
            )}
            {isMobile && (
              <h1 className="text-sm font-semibold text-gray-400 truncate text-right">{sectionTitles[activeSection]}</h1>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              className="relative w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer transition-colors hover:bg-white/5"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <i className="ri-notification-3-line text-gray-400 text-base" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400" />
            </button>

            {/* Çıkış butonu */}
            <button
              onClick={signOut}
              className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer transition-colors hover:bg-red-500/10"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
              title="Çıkış Yap"
            >
              <i className="ri-logout-box-r-line text-gray-400 text-base" />
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 max-w-7xl w-full mx-auto">
          <div className="animate-fade-in" key={activeSection}>
            {renderSection()}
          </div>
        </main>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <AuthGuard>
      <HomeContent />
    </AuthGuard>
  );
}